/**
 * AddIngredientForm Component
 *
 * Inline form for adding new ingredients with:
 * - Name input
 * - Category select
 * - Unit select
 * - Add button
 */

import { useState } from 'react';
import { router } from '@inertiajs/react';

interface AddIngredientFormProps {
  categories: string[];
}

const UNIT_OPTIONS = ['g', 'ml', 'pcs', 'tbsp', 'tsp', 'cups', 'cloves', 'oz', 'lb', 'kg'];

export function AddIngredientForm({ categories }: AddIngredientFormProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [unit, setUnit] = useState('g');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/food/ingredients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({
          ingredient: {
            name: name.trim(),
            category: category || null,
            default_unit: unit,
          },
        }),
      });

      if (response.ok) {
        setName('');
        setCategory('');
        setUnit('g');
        router.reload({ only: ['ingredients'] });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create ingredient');
      }
    } catch {
      setError('Failed to create ingredient');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-gray-50 border-t border-gray-200">
      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          {error}
        </div>
      )}
      <div className="grid grid-cols-[1fr_150px_100px_auto] gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label htmlFor="ingredient-name" className="text-xs font-medium text-gray-500">
            Ingredient Name
          </label>
          <input
            id="ingredient-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Basil"
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            disabled={isSubmitting}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="ingredient-category" className="text-xs font-medium text-gray-500">
            Category
          </label>
          <select
            id="ingredient-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500"
            disabled={isSubmitting}
          >
            <option value="">Select...</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="ingredient-unit" className="text-xs font-medium text-gray-500">
            Unit
          </label>
          <select
            id="ingredient-unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500"
            disabled={isSubmitting}
          >
            {UNIT_OPTIONS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </button>
      </div>
    </form>
  );
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') || '';
}
